import { useEffect, useState } from "react";
import { CustomRoutes, Resource, useStore, useTranslate } from "react-admin";
import { Admin } from "@react-admin/ra-enterprise";
import { Route } from "react-router-dom";
import Box from "@mui/material/Box";
import LockIcon from "@mui/icons-material/Lock";
import Typography from "@mui/material/Typography";
import { authProvider } from "./authProvider";
import { dataProvider } from "./dataProvider";
import { i18nProvider } from "./i18nProvider";
import { resources } from "./resources";
import { CustomLayout } from "./components/CustomLayout";
import { ThemeName, themes } from "./themes/themes";
import { MyLoginPage } from "./myLogin";
import { WebSocketProvider } from "./websocket";
import { API_URL } from "./httpClient";
const LOCAL_DOMAIN =
  import.meta.env.VITE_LOCAL_DOMAIN || window.location.hostname;

const CustomLoadingPage = () => {
  const translate = useTranslate();
  return <div>{translate("ra.page.loading")}</div>;
};

const CustomNotFoundPage = () => {
  const translate = useTranslate();
  return <div>{translate("tenant.not_found")}</div>;
};

const CustomForbiddenPage = () => {
  const translate = useTranslate();
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="80vh"
      gap={2}
    >
      <LockIcon sx={{ fontSize: 64, color: "text.secondary" }} />
      <Typography variant="h5">{translate("forbidden.title")}</Typography>
      <Typography variant="body1" color="text.secondary">
        {translate("forbidden.message")}
      </Typography>
    </Box>
  );
};

const EmptyPage = () => null;

export const App = () => {
  const [tenant, setTenant] = useState<{ name: string; uuid: string } | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const response = await fetch(
          `${API_URL}/public/getTenantBasicInfoByDomain?domain=${LOCAL_DOMAIN}`,
        );
        if (!response.ok) throw new Error("Failed to fetch tenant");

        const { data } = await response.json();
        setTenant(data);
        localStorage.setItem("tenantUuid", data.uuid);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, []);

  const [themeName] = useStore<ThemeName>("themeName", "mytheme");
  const lightTheme = themes.find((theme) => theme.name === themeName)?.light;
  const darkTheme = themes.find((theme) => theme.name === themeName)?.dark;

  return (
    <WebSocketProvider tenantUuid={tenant?.uuid}>
      <Admin
        title={tenant ? `${tenant.name} CRM` : ""}
        authProvider={authProvider}
        dataProvider={dataProvider}
        loginPage={MyLoginPage}
        i18nProvider={i18nProvider}
        requireAuth
        loading={CustomLoadingPage}
        layout={CustomLayout}
        lightTheme={lightTheme}
        darkTheme={darkTheme}
        defaultTheme="dark"
      >
        {resources.map((resource) => (
          <Resource
            key={resource.name}
            name={resource.name}
            list={loading || !tenant ? EmptyPage : resource.list}
            create={loading || !tenant ? EmptyPage : resource.create}
            edit={loading || !tenant ? EmptyPage : resource.edit}
            show={loading || !tenant ? EmptyPage : resource.show}
            icon={resource.icon}
            options={resource.options}
            recordRepresentation={resource.recordRepresentation}
          />
        ))}
        <CustomRoutes>
          <Route path="/forbidden" element={<CustomForbiddenPage />} />
        </CustomRoutes>
        {loading ? (
          <CustomLoadingPage />
        ) : !tenant ? (
          <CustomNotFoundPage />
        ) : null}
      </Admin>
    </WebSocketProvider>
  );
};
